import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { LOGO_URL, BBB_LOGO_URL, PHONE, PHONE_TEL, CERT_LOGOS, logEvent, DISCLAIMER_TITLE, DISCLAIMER_PARAGRAPHS } from "@/lib/api";
import RoofMap from "@/components/RoofMap";
import MaterialSelector from "@/components/MaterialSelector";
import EstimateBreakdown from "@/components/EstimateBreakdown";
import { generateQuotePDF } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  Polygon,
  ArrowUUpLeft,
  Trash,
  FilePdf,
  Ruler,
  House,
  ArrowRight,
  Phone,
  CheckCircle,
} from "@phosphor-icons/react";

export default function Estimator() {
  const [query, setQuery] = useState("");
  const [address, setAddress] = useState("");
  const [center, setCenter] = useState(null);
  const [searching, setSearching] = useState(false);

  const [materials, setMaterials] = useState([]);
  const [pitchOptions, setPitchOptions] = useState([]);
  const [materialId, setMaterialId] = useState("");
  const [pitchIdx, setPitchIdx] = useState(2);
  const [tearoff, setTearoff] = useState(true);
  const [multiStory, setMultiStory] = useState(false);
  const [propertyType, setPropertyType] = useState("residential");

  const [footprint, setFootprint] = useState(0);
  const [estimate, setEstimate] = useState(null);
  const [customer, setCustomer] = useState({ name: "", phone: "", email: "" });
  const [leadSaved, setLeadSaved] = useState(false);
  const [detected, setDetected] = useState(null);

  const mapApiRef = useRef(null);
  const estimatorRef = useRef(null);
  const loggedEstimateRef = useRef(false);
  const leadCreatedRef = useRef(false);
  const leadIdRef = useRef(null);

  const setCust = (k) => (e) => setCustomer((c) => ({ ...c, [k]: e.target.value }));

  useEffect(() => {
    api.get("/config").then(({ data }) => {
      setMaterials(data.materials);
      setPitchOptions(data.pitch_options);
      if (data.materials.length) setMaterialId(data.materials[1]?.id || data.materials[0].id);
    });
  }, []);

  const recalc = useCallback(async () => {
    if (!footprint || !materialId || !pitchOptions.length) {
      setEstimate(null);
      return;
    }
    const pitch = pitchOptions[pitchIdx];
    const { data } = await api.post("/estimate", {
      footprint_sqft: footprint,
      pitch_multiplier: pitch.multiplier,
      pitch_label: pitch.label,
      material_id: materialId,
      tearoff_layers: tearoff ? 1 : 0,
      multi_story: multiStory,
      property_type: propertyType,
    });
    setEstimate(data);
    if (!loggedEstimateRef.current) {
      loggedEstimateRef.current = true;
      logEvent("estimate", { total: data.total, material: data.material_name });
    }
  }, [footprint, materialId, pitchIdx, tearoff, multiStory, propertyType, pitchOptions]);

  useEffect(() => {
    recalc();
  }, [recalc]);

  // Flat-roof materials lock the pitch to Flat
  const selectedMaterial = materials.find((m) => m.id === materialId);
  const isFlat = selectedMaterial?.category === "flat";

  useEffect(() => {
    if (isFlat) setPitchIdx(0);
  }, [isFlat]);

  // Capture the lead automatically once the first estimate is generated for a session,
  // then keep its estimate in sync as the customer adjusts options.
  useEffect(() => {
    if (!estimate) return;
    if (!(customer.name.trim() && customer.email.trim() && customer.phone.trim())) return;

    if (!leadCreatedRef.current) {
      leadCreatedRef.current = true;
      api
        .post("/leads", {
          name: customer.name,
          email: customer.email,
          phone: customer.phone,
          address: address || "Not specified",
          estimate,
        })
        .then((res) => {
          leadIdRef.current = res.data?.id || null;
          logEvent("lead", { total: estimate.total, material: estimate.material_name });
          setLeadSaved(true);
          toast.success("Your details are in — a Roof Fix specialist will reach out shortly.");
        })
        .catch(() => {});
    } else if (leadIdRef.current) {
      api.patch(`/leads/${leadIdRef.current}/estimate`, { estimate }).catch(() => {});
    }
  }, [estimate]); // eslint-disable-line

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!customer.name.trim() || !customer.phone.trim() || !customer.email.trim()) {
      toast.error("Please enter your name, phone, and email first.");
      return;
    }
    if (!query.trim()) return;
    setSearching(true);
    setDetected(null);
    setLeadSaved(false);
    loggedEstimateRef.current = false;
    leadCreatedRef.current = false;
    leadIdRef.current = null;
    try {
      const { data } = await api.post("/roof-detect", { address: query });
      setCenter([data.lat, data.lng]);
      setAddress(data.formatted_address || query);
      mapApiRef.current?.clearAll();
      logEvent("search", { found: data.found, address: data.formatted_address || query });
      if (data.found) {
        setFootprint(data.footprint_sqft);
        setPitchIdx(data.pitch_index);
        setDetected(data);
        toast.success(`Roof auto-detected: ${data.roof_area_sqft.toLocaleString()} sq ft`);
      } else {
        setFootprint(0);
        toast.info("No satellite roof data here — trace the roof manually.");
      }
      setTimeout(() => estimatorRef.current?.scrollIntoView({ behavior: "smooth" }), 300);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed. Try adding city & state.");
    } finally {
      setSearching(false);
    }
  };

  const downloadPdf = async () => {
    if (!estimate) return;
    await generateQuotePDF({ customer: customer || {}, address, estimate });
    toast.success("PDF estimate downloaded");
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      {/* Top nav */}
      <nav className="sticky top-0 z-[1000] flex items-center justify-between px-5 sm:px-8 py-3 bg-white/90 backdrop-blur-xl border-b border-zinc-200 shadow-sm">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Roof Fix Houston" className="h-11 w-11 object-contain" />
          <div className="leading-none">
            <p className="font-chivo font-black tracking-tight text-lg text-zinc-900">ROOF FIX</p>
            <p className="text-[10px] tracking-[0.3em] text-[#EF233C] font-bold">HOUSTON</p>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <a href={`tel:${PHONE_TEL}`} data-testid="nav-phone-link">
            <Button className="rounded-none bg-[#EF233C] hover:bg-[#D90429] text-white text-sm font-bold tracking-wide hidden sm:flex">
              <Phone weight="fill" size={16} className="mr-2" /> {PHONE}
            </Button>
          </a>
          <a href={`tel:${PHONE_TEL}`} className="sm:hidden" aria-label="Call us" data-testid="nav-phone-link-mobile">
            <Button size="icon" className="rounded-none bg-[#EF233C] hover:bg-[#D90429] text-white">
              <Phone weight="fill" size={18} />
            </Button>
          </a>
          <Link to="/login" data-testid="contractor-login-link">
            <Button variant="ghost" className="rounded-none text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 text-sm font-semibold tracking-wide">
              Contractor Login
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-200">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.08]"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1583345237708-add35a664d77?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/70 to-white" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-20 text-center rf-fade-up">
          <span className="inline-block text-xs tracking-[0.3em] uppercase text-[#EF233C] font-bold mb-4">
            Instant Satellite Roofing Estimator • Texas
          </span>
          <h1 className="font-chivo text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-zinc-900">
            Your Roof Estimate.
            <br />
            <span className="text-[#EF233C]">In 60 Seconds.</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-600 max-w-2xl mx-auto mt-5">
            Enter any address — we auto-detect the roof from satellite imagery (or trace it yourself) and give
            you an accurate material, labor, and waste breakdown built on real Texas market rates.
          </p>

          <form onSubmit={handleSearch} className="mt-9 max-w-3xl mx-auto bg-white border border-zinc-200 shadow-xl p-5 sm:p-6 text-left">
            <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-bold mb-3">
              1 · Your Details
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input
                data-testid="lead-name-input"
                required
                value={customer.name}
                onChange={setCust("name")}
                placeholder="Full name"
                className="h-12 bg-white border-zinc-300 rounded-none text-zinc-900 focus-visible:ring-red-500"
              />
              <Input
                data-testid="lead-phone-input"
                required
                value={customer.phone}
                onChange={setCust("phone")}
                placeholder="Phone number"
                className="h-12 bg-white border-zinc-300 rounded-none text-zinc-900 focus-visible:ring-red-500"
              />
              <Input
                data-testid="lead-email-input"
                type="email"
                required
                value={customer.email}
                onChange={setCust("email")}
                placeholder="Email address"
                className="h-12 bg-white border-zinc-300 rounded-none text-zinc-900 focus-visible:ring-red-500"
              />
            </div>

            <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-bold mt-5 mb-3">
              2 · Property Address
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  data-testid="address-search-input"
                  required
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="123 Main St, Houston, TX"
                  className="h-12 pl-11 bg-white border-zinc-300 rounded-none text-base text-zinc-900 focus-visible:ring-red-500"
                />
              </div>
              <Button
                data-testid="address-search-button"
                type="submit"
                disabled={searching}
                className="h-12 px-8 rounded-none bg-[#EF233C] hover:bg-[#D90429] text-white font-bold tracking-wide text-base transition-all duration-200"
              >
                {searching ? "Locating..." : "Start Estimate"}
                <ArrowRight weight="bold" className="ml-2" size={20} />
              </Button>
            </div>
          </form>

          {/* Manufacturer certifications */}
          <div className="mt-10" data-testid="cert-logos">
            <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 font-bold mb-4">
              Factory-Certified & Trusted
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              {CERT_LOGOS.map((c) => (
                <div
                  key={c.id}
                  data-testid={`cert-logo-${c.id}`}
                  className="h-16 w-16 sm:h-[72px] sm:w-[72px] flex items-center justify-center bg-white border border-zinc-200 rounded-md shadow-sm p-1.5 transition-transform duration-200 hover:scale-[1.05]"
                >
                  <img src={c.url} alt={c.alt} className="max-h-full max-w-full object-contain" />
                </div>
              ))}
            </div>
          </div>

          {/* Trust strip */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-8">
            <a
              href="https://www.bbb.org"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bbb-badge-hero"
              className="bg-white px-3 py-2 rounded-md border border-zinc-200 shadow-sm transition-transform duration-200 hover:scale-[1.04]"
            >
              <img src={BBB_LOGO_URL} alt="BBB A+ Accredited Business" className="h-12 w-auto" />
            </a>
            <div className="hidden sm:block h-10 w-px bg-zinc-300" />
            <a href={`tel:${PHONE_TEL}`} data-testid="hero-phone-link" className="flex items-center gap-3 group">
              <span className="flex items-center justify-center h-11 w-11 bg-[#EF233C] group-hover:bg-[#D90429] transition-colors duration-200">
                <Phone weight="fill" size={20} className="text-white" />
              </span>
              <span className="text-left">
                <span className="block text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-bold">Call for a quote</span>
                <span className="block font-barlow text-2xl font-bold text-zinc-900 leading-none">{PHONE}</span>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Estimator workspace */}
      <section ref={estimatorRef} className="max-w-7xl mx-auto px-3 sm:px-8 py-12">
        <div className="grid lg:grid-cols-[1fr_420px] gap-4">
          {/* Map */}
          <div className="relative border border-zinc-200 bg-zinc-100 h-[480px] lg:h-[680px] shadow-sm">
            {center && (
              <div className="absolute top-3 left-3 z-[500] flex flex-wrap gap-2">
                <Button
                  data-testid="draw-roof-button"
                  onClick={() => mapApiRef.current?.startDraw()}
                  className="h-9 rounded-none bg-[#EF233C] hover:bg-[#D90429] text-white text-xs font-bold tracking-wide shadow"
                >
                  <Polygon weight="bold" size={16} className="mr-1.5" /> Trace Roof
                </Button>
                <Button
                  data-testid="undo-button"
                  onClick={() => mapApiRef.current?.undo()}
                  className="h-9 rounded-none bg-white/95 backdrop-blur border border-zinc-300 hover:bg-zinc-100 text-zinc-800 text-xs shadow"
                >
                  <ArrowUUpLeft weight="bold" size={16} />
                </Button>
                <Button
                  data-testid="clear-button"
                  onClick={() => mapApiRef.current?.clearAll()}
                  className="h-9 rounded-none bg-white/95 backdrop-blur border border-zinc-300 hover:bg-zinc-100 text-zinc-800 text-xs shadow"
                >
                  <Trash weight="bold" size={16} />
                </Button>
              </div>
            )}

            {center && detected && footprint > 0 && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] bg-emerald-50 border border-emerald-300 text-emerald-700 px-3 py-1.5 text-[11px] font-semibold tracking-wide whitespace-nowrap shadow" data-testid="auto-detected-banner">
                ✓ Auto-detected via satellite • {detected.segment_count} segments • {detected.avg_pitch_degrees}° avg pitch
              </div>
            )}

            {center && footprint > 0 && (
              <div className="absolute bottom-3 left-3 z-[500] bg-white/95 backdrop-blur border border-zinc-300 px-4 py-2 shadow">
                <p className="text-[10px] tracking-wider uppercase text-zinc-500 font-bold">
                  {detected ? "Detected Roof Footprint" : "Traced Footprint"}
                </p>
                <p data-testid="footprint-value" className="font-barlow text-2xl font-bold leading-none text-zinc-900">
                  {footprint.toLocaleString()} <span className="text-sm text-zinc-500">sq ft</span>
                </p>
              </div>
            )}

            {!center ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-6">
                <House size={56} weight="duotone" className="text-zinc-300 mb-4" />
                <p className="text-zinc-600 font-semibold">Search an address to load satellite imagery</p>
                <p className="text-zinc-400 text-sm mt-1">We auto-detect the roof — or trace it yourself.</p>
              </div>
            ) : (
              <RoofMap
                center={center}
                onAreaChange={(v) => {
                  setFootprint(v);
                  if (v > 0) setDetected(null);
                }}
                onReady={(apiObj) => (mapApiRef.current = apiObj)}
              />
            )}
          </div>

          {/* Control panel */}
          <div className="space-y-4">
            <div className="bg-white border border-zinc-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Ruler weight="bold" size={18} className="text-[#EF233C]" />
                <h3 className="font-chivo text-lg font-bold tracking-tight text-zinc-900">Configure Roof</h3>
              </div>

              <label className="text-xs tracking-wider uppercase text-zinc-500 font-bold">Property Type</label>
              <div className="mt-1 mb-4 grid grid-cols-2 gap-2" data-testid="property-type-selector">
                {[
                  { id: "residential", label: "Residential" },
                  { id: "commercial", label: "Commercial" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`property-type-${opt.id}`}
                    onClick={() => setPropertyType(opt.id)}
                    className={`h-11 border text-sm font-bold tracking-wide transition-all duration-200 ${
                      propertyType === opt.id
                        ? "border-[#EF233C] bg-[#EF233C] text-white"
                        : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-100"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <label className="text-xs tracking-wider uppercase text-zinc-500 font-bold">Roof Pitch</label>
              <Select value={String(pitchIdx)} onValueChange={(v) => setPitchIdx(Number(v))} disabled={isFlat}>
                <SelectTrigger data-testid="pitch-select" className="mt-1 mb-1 bg-white border-zinc-300 rounded-none h-11 text-zinc-900 focus:ring-red-500 disabled:opacity-60 disabled:cursor-not-allowed">
                  <SelectValue placeholder="Select pitch" />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-200 text-zinc-900 rounded-none">
                  {pitchOptions.map((p, i) => (
                    <SelectItem key={i} value={String(i)} className="focus:bg-zinc-100">
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-400 mb-4">
                {isFlat ? "Pitch locked to flat for low-slope roofing systems." : "\u00A0"}
              </p>

              <label className="text-xs tracking-wider uppercase text-zinc-500 font-bold">Material</label>
              <div className="mt-2 mb-4">
                <MaterialSelector materials={materials} selectedId={materialId} onSelect={setMaterialId} />
              </div>

              <div className="flex items-center justify-between border border-zinc-200 bg-zinc-50 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-zinc-900">Tear-off existing roof</p>
                  <p className="text-xs text-zinc-500">Removal + disposal of old roofing</p>
                </div>
                <Switch data-testid="tearoff-switch" checked={tearoff} onCheckedChange={setTearoff} />
              </div>

              <div className="flex items-center justify-between border border-zinc-200 bg-zinc-50 px-4 py-3 mt-3">
                <div>
                  <p className="text-sm font-semibold text-zinc-900">Multi-story structure</p>
                  <p className="text-xs text-zinc-500">Adds 15% for added height &amp; complexity</p>
                </div>
                <Switch data-testid="multistory-switch" checked={multiStory} onCheckedChange={setMultiStory} />
              </div>
            </div>

            <div className="bg-white border border-zinc-200 p-5 shadow-sm">
              {estimate ? (
                <>
                  <EstimateBreakdown estimate={estimate} />
                  {leadSaved && (
                    <div data-testid="lead-saved-banner" className="mt-4 flex items-start gap-2 bg-emerald-50 border border-emerald-300 text-emerald-700 px-4 py-3">
                      <CheckCircle weight="fill" size={20} className="mt-0.5 shrink-0" />
                      <p className="text-sm font-semibold">
                        We've got your details, {customer.name.split(" ")[0]}. A Roof Fix Houston specialist will
                        reach out to confirm your quote.
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-1 gap-2 mt-5">
                    <Button
                      data-testid="download-pdf-button"
                      onClick={downloadPdf}
                      className="h-12 rounded-none bg-[#EF233C] hover:bg-[#D90429] text-white font-bold tracking-wide transition-all duration-200"
                    >
                      <FilePdf weight="bold" size={18} className="mr-2" /> Download PDF Estimate
                    </Button>
                    <a href={`tel:${PHONE_TEL}`} data-testid="estimate-phone-link">
                      <Button
                        variant="outline"
                        className="w-full h-12 rounded-none border-zinc-300 bg-white hover:bg-zinc-100 text-zinc-900 font-bold tracking-wide"
                      >
                        <Phone weight="fill" size={18} className="mr-2" /> Call {PHONE}
                      </Button>
                    </a>
                  </div>
                </>
              ) : (
                <div className="text-center py-10">
                  <Polygon size={40} weight="duotone" className="text-zinc-300 mx-auto mb-3" />
                  <p className="text-zinc-500 font-semibold text-sm">
                    {center ? 'Trace the roof to generate your estimate' : 'Search an address to begin'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {estimate && (
          <div className="mt-8 border border-zinc-200 bg-zinc-50 p-5 sm:p-6" data-testid="estimate-disclaimer">
            <p className="font-chivo text-sm font-bold tracking-tight text-zinc-900 mb-2">{DISCLAIMER_TITLE}</p>
            {DISCLAIMER_PARAGRAPHS.map((p, i) => (
              <p key={i} className="text-xs leading-relaxed text-zinc-500 mb-2 last:mb-0">{p}</p>
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-zinc-200 bg-zinc-50 py-10">
        <div className="max-w-5xl mx-auto px-5 flex flex-col items-center gap-5 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3" data-testid="cert-logos-footer">
            {CERT_LOGOS.map((c) => (
              <div key={c.id} className="h-14 w-14 flex items-center justify-center bg-white border border-zinc-200 rounded-md shadow-sm p-1.5">
                <img src={c.url} alt={c.alt} className="max-h-full max-w-full object-contain" />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-6">
            <img src={LOGO_URL} alt="Roof Fix Houston" className="h-12 w-12 object-contain" />
            <a href="https://www.bbb.org" target="_blank" rel="noopener noreferrer" data-testid="bbb-badge-footer" className="bg-white px-3 py-2 rounded-md border border-zinc-200 shadow-sm transition-transform duration-200 hover:scale-[1.04]">
              <img src={BBB_LOGO_URL} alt="BBB A+ Accredited Business" className="h-11 w-auto" />
            </a>
          </div>
          <a href={`tel:${PHONE_TEL}`} data-testid="footer-phone-link" className="inline-flex items-center gap-2 text-zinc-900 font-bold hover:text-[#EF233C] transition-colors">
            <Phone weight="fill" size={18} className="text-[#EF233C]" /> {PHONE}
          </a>
          <p className="text-sm text-zinc-500">
            Roof Fix Houston • BBB A+ Accredited • Serving the Greater Houston & Texas area
            <br />
            Estimates are preliminary, subject to on-site inspection.
          </p>
        </div>
      </footer>
    </div>
  );
}
